import { EMAIL_REGEXP } from '@pbk/utils';
import { z } from 'zod';

import { createPhoneSchema } from '../../../const/zod/phoneSchema';
import {
	FIRST_NAME_OR_LAST_NAME_MAX_LENGTH,
	FIRST_NAME_OR_LAST_NAME_REGEX,
	IDENTITY_CARD_NUMBER_LENGTH,
	IDENTITY_CARD_NUMBER_REGEX,
	MAX_BIRTHDAY_DATE,
	MIN_BIRTHDAY_DATE,
	PID_UUID_LENGTH,
} from '../../../const/zod/zod';

import { birthNumberRefine } from './birthNumberRefine';
import type { GetAppAdvancedSearchFormSchemaErrorMessages } from './types/types';

export const getAppAdvancedSearchFormSchema = ({
	alphabetErrorText,
	numberErrorText,
	emailErrorText,
	atLeastOneFieldMustBeFilledInText,
	noSlashText,
	dateMustBeAfterOrEqualMinText,
	dateMustBeBeforeOrEqualTodayText,
	getMaxStringLengthText,
	getBirthNumberRemainderText,
	birthNumberInvalidText,
	getBirthNumberLengthText,
	getPhoneLengthError,
}: GetAppAdvancedSearchFormSchemaErrorMessages) => {
	const appAdvancedSearchFormSchema = z
		.object({
			lastName: z
				.string()
				.max(
					FIRST_NAME_OR_LAST_NAME_MAX_LENGTH,
					getMaxStringLengthText({ max: FIRST_NAME_OR_LAST_NAME_MAX_LENGTH }),
				)
				.optional()
				.nullable()
				.refine((val) => !val || FIRST_NAME_OR_LAST_NAME_REGEX.test(val), {
					message: alphabetErrorText,
				}),
			firstName: z
				.string()
				.max(
					FIRST_NAME_OR_LAST_NAME_MAX_LENGTH,
					getMaxStringLengthText({ max: FIRST_NAME_OR_LAST_NAME_MAX_LENGTH }),
				)
				.optional()
				.nullable()
				.refine((val) => !val || FIRST_NAME_OR_LAST_NAME_REGEX.test(val), {
					message: alphabetErrorText,
				}),
			identityCardNumber: z
				.string()
				.max(
					IDENTITY_CARD_NUMBER_LENGTH,
					getMaxStringLengthText({ max: IDENTITY_CARD_NUMBER_LENGTH }),
				)
				.optional()
				.nullable()
				.refine((val) => !val || IDENTITY_CARD_NUMBER_REGEX.test(val), {
					message: numberErrorText,
				}),
			birthNumber: z
				.string()
				.optional()
				.nullable()
				.refine((val) =>
					birthNumberRefine({
						value: val,
						numberErrorText,
						noSlashText,
						getBirthNumberRemainderText,
						birthNumberInvalidText,
						getBirthNumberLengthText,
					}),
				),
			birthday: z.coerce
				.date()
				.min(MIN_BIRTHDAY_DATE, { message: dateMustBeAfterOrEqualMinText })
				.max(MAX_BIRTHDAY_DATE, { message: dateMustBeBeforeOrEqualTodayText })
				.optional()
				.nullable(),
			phone: createPhoneSchema({ getPhoneLengthError }),

			email: z
				.email({ error: emailErrorText, pattern: EMAIL_REGEXP })
				.or(z.literal(''))
				.optional()
				.nullable(),
			pidUuid: z.coerce
				.string()
				.refine((value) => z.uuid().safeParse(value).success, {
					message: getMaxStringLengthText({ max: PID_UUID_LENGTH }),
				})
				.optional()
				.nullable()
				.or(z.literal('')),
		})
		.refine(
			(data) =>
				Object.values(data).some((value) => {
					if (value === null || value === undefined) return false;
					if (typeof value === 'string' && value.trim() === '') return false;
					return true;
				}),
			{
				message: atLeastOneFieldMustBeFilledInText,
			},
		);

	return appAdvancedSearchFormSchema;
};

export type AppAdvancedSearchForm = z.infer<
	ReturnType<typeof getAppAdvancedSearchFormSchema>
>;
